import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import StudentPicker from "../../components/StudentPicker";
import { listCashEquivalentLedgers } from "../../api/accountApi";
import {
  listInvoicesForStudent,
  listInvoicesBetween,
  deleteInvoice,
  getMoneyReceipt,
  getReceiptTemplate,
  saveReceiptTemplate,
} from "../../api/feeApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

function downloadCsv(filename, rows, columns) {
  const escape = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const csv = [columns.map((c) => escape(c.label)).join(",")]
    .concat(rows.map((r) => columns.map((c) => escape(c.value(r))).join(",")))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Direct port of digicampusbd's Fee Invoice List / Paid List / Invoice
 * Delete pages, plus the Money Receipt Template selector — one screen: find
 * invoices (by student, or by date range for everyone), view the receipt,
 * delete (reverses the ledger posting, see FeeInvoiceService.deleteInvoice).
 *
 * Date-range mode ("Fee Invoice List", `fee-report-all-paid-list`) matches
 * the real old-app page's own real feature set — found missing while
 * sweeping Fee Management (see MIGRATION.md): a required ledger filter
 * (`/general/accounts/report/cash-equivalance/dfps/ledger/list`, the same
 * {102,103} set as `FeeCollectSummaryPage`/`listCashEquivalentLedgers` — left
 * un-required here, an empty selection shows every ledger, matching this
 * migration's own looser convention elsewhere) plus class-grouped subtotal
 * rows and a grand total (`allPaidInvoiceList`'s own grouping). CSV export
 * substitutes for the real page's client-side Excel/PDF export (jsPDF/xlsx);
 * the existing "View Receipt" modal + browser print substitutes for the real
 * page's one-click Jasper-report PDF download — neither Jasper reports nor
 * jsPDF/xlsx exports are ported anywhere else in this migration.
 */
export default function FeeInvoiceListPage() {
  const [mode, setMode] = useState("student");
  const [student, setStudent] = useState(null);
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [templateDefaultId, setTemplateDefaultId] = useState(1);
  const [ledgers, setLedgers] = useState([]);
  const [ledgerIds, setLedgerIds] = useState([]);

  useEffect(() => {
    getReceiptTemplate().then((t) => setTemplateDefaultId(t.defaultId));
    listCashEquivalentLedgers().then(setLedgers);
  }, []);

  const toggleLedger = (id) => setLedgerIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const load = () => {
    setError("");
    if (mode === "student") {
      if (!student) {
        setInvoices([]);
        return;
      }
      setLoading(true);
      listInvoicesForStudent(student.identificationId)
        .then(setInvoices)
        .catch(() => setError("Failed to load invoices."))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      listInvoicesBetween(fromDate, toDate)
        .then(setInvoices)
        .catch(() => setError("Failed to load invoices."))
        .finally(() => setLoading(false));
    }
  };

  useEffect(load, [mode, student, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleView = async (invoice) => {
    setError("");
    try {
      const data = await getMoneyReceipt(invoice.masterId);
      setReceipt(data);
    } catch (err) {
      setError("Failed to load receipt.");
    }
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoiceId}? This reverses its ledger entries.`)) return;
    setError("");
    try {
      await deleteInvoice(invoice.masterId);
      setSuccess("Invoice deleted and ledger entries reversed.");
      setInvoices((prev) => prev.filter((i) => i.masterId !== invoice.masterId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete invoice.");
    }
  };

  const handleTemplateSave = async () => {
    try {
      await saveReceiptTemplate({ defaultId: Number(templateDefaultId) });
      setSuccess("Receipt template saved.");
    } catch (err) {
      setError("Failed to save template.");
    }
  };

  // Real old-app grouping: date-range invoices filtered by ledger (empty
  // selection = every ledger), grouped by class with a subtotal row each,
  // plus a grand total — direct port of `allPaidInvoiceList`'s own grouping.
  const filteredInvoices = useMemo(() => {
    if (mode !== "date-range") return invoices;
    return ledgerIds.length === 0 ? invoices : invoices.filter((inv) => ledgerIds.includes(String(inv.ledgerId)));
  }, [invoices, ledgerIds, mode]);

  const groupedByClass = useMemo(() => {
    if (mode !== "date-range") return [];
    const byClass = new Map();
    filteredInvoices.forEach((inv) => {
      const key = inv.className || "(No Class)";
      if (!byClass.has(key)) byClass.set(key, { className: key, rows: [], subtotal: 0 });
      const group = byClass.get(key);
      group.rows.push(inv);
      group.subtotal += inv.paidAmount || 0;
    });
    return Array.from(byClass.values());
  }, [filteredInvoices, mode]);

  const grandTotal = groupedByClass.reduce((sum, g) => sum + g.subtotal, 0);

  const handleExportCsv = () => {
    downloadCsv(`fee-invoice-list-${fromDate}-to-${toDate}.csv`, filteredInvoices, [
      { label: "Payment Date", value: (r) => r.paymentDate },
      { label: "Invoice Id", value: (r) => r.invoiceId },
      { label: "Student Id", value: (r) => r.customStudentId },
      { label: "Name", value: (r) => r.studentName },
      { label: "Roll", value: (r) => r.studentRoll },
      { label: "Class", value: (r) => r.className },
      { label: "Section", value: (r) => r.sectionName },
      { label: "Payment Ledger", value: (r) => r.ledgerName },
      { label: "Paid Amount", value: (r) => r.paidAmount },
    ]);
  };

  const columns = [
    { key: "invoiceId", label: "Invoice #" },
    { key: "studentName", label: "Student" },
    { key: "customStudentId", label: "Student ID" },
    { key: "paymentDate", label: "Payment Date" },
    { key: "payableAmount", label: "Payable" },
    { key: "paidAmount", label: "Paid" },
    { key: "dueAmount", label: "Due" },
    { key: "username", label: "Collected By" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Invoices</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: "0.85rem" }}>
            Receipt Template ID:{" "}
            <input type="number" style={{ width: 60 }} value={templateDefaultId} onChange={(e) => setTemplateDefaultId(e.target.value)} />
          </label>
          <button type="button" className="btn-secondary" onClick={handleTemplateSave}>
            Save Template
          </button>
        </div>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Find By
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="student">Student</option>
            <option value="date-range">Date Range</option>
          </select>
        </label>
        {mode === "date-range" && (
          <>
            <label>
              From
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
          </>
        )}
      </div>

      {mode === "date-range" && ledgers.length > 0 && (
        <div className="form-row">
          <span className="admit-card-label">Filter by Ledger:</span>{" "}
          {ledgers.map((l) => (
            <label key={l.ledgerId} className="checkbox-inline" style={{ display: "inline-flex", marginRight: 12 }}>
              <input type="checkbox" checked={ledgerIds.includes(String(l.ledgerId))} onChange={() => toggleLedger(String(l.ledgerId))} />
              {l.ledgerName}
            </label>
          ))}
        </div>
      )}

      {mode === "student" && <StudentPicker onSelect={setStudent} />}

      {loading ? (
        <p>Loading...</p>
      ) : mode === "date-range" ? (
        <>
          {invoices.length > 0 && (
            <div className="form-actions" style={{ justifyContent: "flex-end" }}>
              <button type="button" className="btn-secondary" onClick={handleExportCsv}>
                Export CSV
              </button>
            </div>
          )}
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th>Payment Date</th>
                  <th>Invoice #</th>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Roll</th>
                  <th>Section</th>
                  <th>Payment Ledger</th>
                  <th>Paid Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {groupedByClass.map((group) => (
                  <React.Fragment key={group.className}>
                    <tr className="group-row">
                      <td colSpan={9}>
                        <strong>{group.className}</strong>
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.masterId}>
                        <td>{row.paymentDate}</td>
                        <td>{row.invoiceId}</td>
                        <td>{row.customStudentId}</td>
                        <td>{row.studentName}</td>
                        <td>{row.studentRoll}</td>
                        <td>{row.sectionName}</td>
                        <td>{row.ledgerName}</td>
                        <td>{Math.round(row.paidAmount)}</td>
                        <td>
                          <button type="button" className="btn-link" onClick={() => handleView(row)}>
                            View Receipt
                          </button>{" "}
                          <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr className="subtotal-row">
                      <td colSpan={7} style={{ textAlign: "right" }}>
                        <strong>Sub Total</strong>
                      </td>
                      <td>
                        <strong>{Math.round(group.subtotal)}</strong>
                      </td>
                      <td></td>
                    </tr>
                  </React.Fragment>
                ))}
                {groupedByClass.length === 0 && (
                  <tr>
                    <td colSpan={9}>No invoices found for this selection.</td>
                  </tr>
                )}
              </tbody>
              {groupedByClass.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={7} style={{ textAlign: "right" }}>
                      <strong>Grand Total</strong>
                    </td>
                    <td>
                      <strong>{Math.round(grandTotal)}</strong>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      ) : (
        <DataTable
          columns={columns}
          rows={invoices}
          actions={(row) => (
            <>
              <button type="button" className="btn-link" onClick={() => handleView(row)}>
                View Receipt
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {receipt && (
        <Modal title={`Money Receipt — ${receipt.invoiceId}`} onClose={() => setReceipt(null)} footer={<button type="button" className="btn-primary" onClick={() => window.print()}>Print</button>}>
          <p>
            <strong>{receipt.studentName}</strong> ({receipt.customStudentId}) — {receipt.className}, Roll {receipt.roll}
          </p>
          <p>
            Payment Date: {receipt.paymentDate} &nbsp; By: {receipt.username}
          </p>
          <div className="table-scroll">
          <table className="attendance-table">
            <thead>
              <tr>
                <th>Fee Head</th>
                <th>Sub-Head</th>
                <th>Payable</th>
                <th>Waiver</th>
                <th>Fine</th>
                <th>Paid</th>
              </tr>
            </thead>
            <tbody>
              {receipt.lines.map((l, idx) => (
                <tr key={idx}>
                  <td>{l.feeHeadName}</td>
                  <td>{l.feeSubHeadName}</td>
                  <td>{l.payable}</td>
                  <td>{l.waiver}</td>
                  <td>{l.fine}</td>
                  <td>{l.paid}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <p>
            <strong>Total Paid: {receipt.totalPaid}</strong> ({receipt.paidInWord})
          </p>
          {receipt.note && <p>Note: {receipt.note}</p>}
        </Modal>
      )}
    </MainLayout>
  );
}
